// TIBECO - Organigrama Digitală - Echipa completă de 11 angajați specializați în ferestre și uși
import React, { useState } from 'react';
import { 
  Users, Search, Filter, ZoomIn, ZoomOut, Plus, Edit, Eye, Phone, Mail, MapPin, 
  Calendar, Award, ChevronDown, ChevronRight, Building, Star, Crown, Heart, User, 
  Clock, Factory, Wrench, PenTool, TrendingUp, Package, DollarSign
} from 'lucide-react';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import { mockData, getDepartmentColor } from '../../utils/mockData';

const OrganigramaDigitala = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(['management']);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');

  // Structura organizațională TIBECO - 11 angajați organizați pe departamente
  const orgStructure = {
    management: {
      title: 'Management',
      icon: Crown,
      color: 'text-primary-600',
      employees: mockData.employees.filter(emp => emp.department === 'Management'),
      children: ['tehnica', 'productie', 'montaj', 'administrare']
    },
    tehnica: {
      title: 'Departament Tehnică',
      icon: PenTool,
      color: 'text-accent-blue',
      employees: mockData.employees.filter(emp => emp.department === 'Tehnică'),
      children: []
    },
    productie: {
      title: 'Departament Producție',
      icon: Factory,
      color: 'text-primary-600',
      employees: mockData.employees.filter(emp => emp.department === 'Producție'),
      children: []
    },
    montaj: {
      title: 'Echipa Montaj',
      icon: Wrench,
      color: 'text-secondary-600',
      employees: mockData.employees.filter(emp => emp.department === 'Montaj'),
      children: []
    },
    administrare: {
      title: 'Administrație & Support',
      icon: Building,
      color: 'text-purple-600',
      employees: mockData.employees.filter(emp => 
        ['Administrație', 'Marketing', 'Logistică'].includes(emp.department)
      ),
      children: []
    }
  };

  // Statistici organizaționale
  const orgStats = {
    totalEmployees: mockData.employees.length,
    departments: Object.keys(orgStructure).length,
    avgExperience: Math.round(
      mockData.employees.reduce((sum, emp) => {
        const experience = Math.floor((new Date() - new Date(emp.startDate)) / (365.25 * 24 * 60 * 60 * 1000));
        return sum + experience;
      }, 0) / mockData.employees.length
    ),
    activeEmployees: mockData.employees.filter(emp => emp.status === 'Activ').length
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeModal(true);
  };

  const getDepartmentIcon = (department) => {
    switch (department) {
      case 'Management': return Crown;
      case 'Tehnică': return PenTool;
      case 'Producție': return Factory;
      case 'Montaj': return Wrench;
      case 'Marketing': return TrendingUp;
      case 'Administrație': return Building;
      case 'Logistică': return Package;
      default: return User;
    }
  };

  const getExperience = (startDate) => {
    const years = Math.floor((new Date() - new Date(startDate)) / (365.25 * 24 * 60 * 60 * 1000));
    return years;
  };

  const filteredEmployees = mockData.employees.filter(employee =>
    employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
    employee.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Tree Node Component
  const TreeNode = ({ nodeId, data, level = 0 }) => {
    const isExpanded = expandedNodes.includes(nodeId);
    const hasChildren = data.children && data.children.length > 0;
    const Icon = data.icon;

    return (
      <div className="ml-4" style={{ marginLeft: `${level * 24}px` }}>
        <div 
          className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-3 cursor-pointer hover:shadow-md transition-all"
          onClick={() => hasChildren && toggleNode(nodeId)}
        >
          {hasChildren && (
            <button className="mr-2">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
          
          <Icon className={`w-6 h-6 mr-3 ${data.color}`} />
          
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white">{data.title}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {data.employees.length} angajat{data.employees.length !== 1 ? 'i' : ''}
            </p>
          </div>
          
          <Badge variant="secondary" className="text-xs">
            {data.employees.length}
          </Badge>
        </div>

        {/* Employee Cards */}
        {(isExpanded || level === 0) && (
          <div className="ml-6 space-y-2 mb-4">
            {data.employees.map((employee, index) => {
              const DeptIcon = getDepartmentIcon(employee.department);
              return (
                <div
                  key={employee.id}
                  className="flex items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  onClick={() => handleEmployeeClick(employee)}
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center mr-3">
                    <DeptIcon className="w-5 h-5 text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                      {employee.name}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {employee.position}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <Badge variant={getDepartmentColor(employee.department)} className="text-xs mb-1">
                      {employee.department}
                    </Badge>
                    <p className="text-xs text-gray-500">
                      {getExperience(employee.startDate)} ani exp.
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Children Nodes */}
        {isExpanded && hasChildren && (
          <div className="ml-2">
            {data.children.map((childId) => (
              <TreeNode 
                key={childId} 
                nodeId={childId} 
                data={orgStructure[childId]} 
                level={level + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Factory className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <Users className="w-6 h-6 mr-2 text-primary-600" />
              Organigrama TIBECO
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Structura organizațională completă - Ferestre și Uși • {orgStats.totalEmployees} angajați activi
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg">
            <button 
              onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-l-lg"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="px-3 py-2 text-sm font-medium">
              {zoomLevel}%
            </span>
            <button 
              onClick={() => setZoomLevel(Math.min(150, zoomLevel + 10))}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-r-lg"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
          
          <button className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Angajat Nou
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Angajați</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{orgStats.totalEmployees}</p>
              <p className="text-sm text-green-600">{orgStats.activeEmployees} activi</p>
            </div>
            <Users className="w-8 h-8 text-primary-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Departamente</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{orgStats.departments}</p>
              <p className="text-sm text-gray-500">Structura organizațională</p>
            </div>
            <Building className="w-8 h-8 text-secondary-600" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Experiență Medie</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{orgStats.avgExperience} ani</p>
              <p className="text-sm text-gray-500">În industria construcțiilor</p>
            </div>
            <Award className="w-8 h-8 text-accent-blue" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Specializări</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">8+</p>
              <p className="text-sm text-gray-500">Competențe cheie</p>
            </div>
            <Star className="w-8 h-8 text-yellow-600" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Organigrama Vizuală */}
        <div className="xl:col-span-3">
          <Card title="Structura Organizațională TIBECO" icon={Building}>
            <div className="mb-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Caută angajați..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            
            <div 
              className="overflow-auto p-4"
              style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top left' }}
            >
              {Object.entries(orgStructure).map(([nodeId, data]) => (
                <TreeNode key={nodeId} nodeId={nodeId} data={data} />
              ))}
            </div>
          </Card>
        </div>

        {/* Departamente Overview */}
        <div>
          <Card title="Departamente TIBECO" icon={Factory}>
            <div className="space-y-4">
              {Object.entries(orgStructure).map(([nodeId, data]) => {
                const Icon = data.icon;
                return (
                  <div key={nodeId} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <Icon className={`w-5 h-5 mr-2 ${data.color}`} />
                        <span className="font-medium text-sm text-gray-900 dark:text-white">
                          {data.title}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {data.employees.length}
                      </Badge>
                    </div>
                    
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      {data.employees.map(emp => emp.position).join(', ').substring(0, 50)}
                      {data.employees.map(emp => emp.position).join(', ').length > 50 && '...'}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card title="Acțiuni Rapide" icon={Edit} className="mt-6">
            <div className="space-y-3">
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center">
                  <Plus className="w-4 h-4 text-primary-600 mr-3" />
                  <div>
                    <p className="font-medium text-sm">Angajat Nou</p>
                    <p className="text-xs text-gray-500">Adaugă membru echipă</p>
                  </div>
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center">
                  <Building className="w-4 h-4 text-secondary-600 mr-3" />
                  <div>
                    <p className="font-medium text-sm">Restructurare</p>
                    <p className="text-xs text-gray-500">Modifică organigrama</p>
                  </div>
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center">
                  <Award className="w-4 h-4 text-accent-blue mr-3" />
                  <div>
                    <p className="font-medium text-sm">Evaluări</p>
                    <p className="text-xs text-gray-500">Performanță echipă</p>
                  </div>
                </div>
              </button>
              
              <button className="w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors">
                <div className="flex items-center">
                  <Eye className="w-4 h-4 text-purple-600 mr-3" />
                  <div>
                    <p className="font-medium text-sm">Export PDF</p>
                    <p className="text-xs text-gray-500">Organigrama oficială</p>
                  </div>
                </div>
              </button>
            </div>
          </Card>
        </div>
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <Modal
          isOpen={showEmployeeModal}
          onClose={() => {
            setShowEmployeeModal(false);
            setSelectedEmployee(null);
          }}
          title={`Detalii Angajat - ${selectedEmployee.name}`}
          size="lg"
        >
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center">
                {React.createElement(getDepartmentIcon(selectedEmployee.department), {
                  className: "w-8 h-8 text-white"
                })}
              </div>
              
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {selectedEmployee.name}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">{selectedEmployee.position}</p>
                <div className="flex items-center space-x-2 mt-2">
                  <Badge variant={getDepartmentColor(selectedEmployee.department)}>
                    {selectedEmployee.department}
                  </Badge>
                  <Badge variant="success">
                    {selectedEmployee.status}
                  </Badge>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Contact</h4>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm">{selectedEmployee.email}</span>
                    </div>
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm">{selectedEmployee.phone}</span>
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm">
                        Angajat din: {new Date(selectedEmployee.startDate).toLocaleDateString('ro-RO')}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Specializare</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedEmployee.specialization}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Competențe</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedEmployee.skills?.map((skill, index) => (
                      <Badge key={index} variant="info" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Program Lucru</h4>
                  <div className="space-y-1 text-sm">
                    {Object.entries(selectedEmployee.workingHours || {}).map(([day, hours]) => (
                      <div key={day} className="flex justify-between">
                        <span className="capitalize text-gray-600 dark:text-gray-400">
                          {day === 'monday' ? 'Luni' :
                           day === 'tuesday' ? 'Marți' :
                           day === 'wednesday' ? 'Miercuri' :
                           day === 'thursday' ? 'Joi' :
                           day === 'friday' ? 'Vineri' :
                           day === 'saturday' ? 'Sâmbătă' :
                           'Duminică'}:
                        </span>
                        <span className={`font-medium ${
                          hours === 'Liber' || hours === 'Concediu' ? 'text-green-600' : 'text-gray-900 dark:text-white'
                        }`}>
                          {hours}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Status special pentru Noemia */}
                {selectedEmployee.status === 'Concediu maternal' && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Status special:</strong> Concediu creștere copil până în ianuarie 2025
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t dark:border-gray-700">
              <button
                onClick={() => {
                  setShowEmployeeModal(false);
                  setSelectedEmployee(null);
                }}
                className="btn-secondary"
              >
                Închide
              </button>
              <button className="btn-primary">
                <Edit className="w-4 h-4 mr-2" />
                Editează Profil
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrganigramaDigitala;
